import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

/** Stop ramp & persentase mix dari warna dasar (500). + = ke putih, − = ke hitam. */
const RAMP: { stop: number; mix: number }[] = [
	{ stop: 50, mix: 0.95 },
	{ stop: 100, mix: 0.88 },
	{ stop: 200, mix: 0.74 },
	{ stop: 300, mix: 0.56 },
	{ stop: 400, mix: 0.3 },
	{ stop: 500, mix: 0 },
	{ stop: 600, mix: -0.12 },
	{ stop: 700, mix: -0.26 },
	{ stop: 800, mix: -0.4 },
	{ stop: 900, mix: -0.52 },
	{ stop: 950, mix: -0.68 },
];

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb | null {
	let h = (hex || '').trim().replace(/^#/, '');
	if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
	if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
	return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Geser channel ke putih (amt>0) atau hitam (amt<0). */
function mixChannel(c: number, amt: number): number {
	return amt >= 0 ? Math.round(c + (255 - c) * amt) : Math.round(c * (1 + amt));
}

function rgbToHex([r, g, b]: Rgb): string {
	const h = (n: number) => n.toString(16).padStart(2, '0');
	return `#${h(r)}${h(g)}${h(b)}`;
}

/** Bangun deklarasi CSS var --tw-color-<prefix>-<stop> untuk seluruh ramp.
 *  PENTING: Tailwind v4 dgn `prefix: 'tw'` meng-emit token @theme sebagai
 *  --tw-color-* dan utilitas membaca var(--tw-color-*). Nama HARUS diprefiks `tw`
 *  agar override runtime benar-benar mengenai utilitas (tw:bg-brand-*, dst.). */
function rampVars(prefix: string, hex: string | null | undefined): string {
	const base = hex ? hexToRgb(hex) : null;
	if (!base) return '';
	return RAMP.map(({ stop, mix }) => {
		const c: Rgb = [
			mixChannel(base[0], mix),
			mixChannel(base[1], mix),
			mixChannel(base[2], mix),
		];
		return `--tw-color-${prefix}-${stop}:${rgbToHex(c)};`;
	}).join('');
}

/**
 * Tema warna landing per tenant. Dari 1 hex primary & 1 hex accent, hasilkan ramp
 * 50–950 lalu override CSS var Tailwind v4 (--tw-color-brand-* & --tw-color-gold-*) lewat
 * <style id="tenant-theme"> di <head>. Dipanggil saat SSR (sebelum render) → tanpa FOUC.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
	private readonly doc = inject(DOCUMENT);

	apply(primary: string | null | undefined, accent: string | null | undefined): void {
		const vars = `${rampVars('brand', primary)}${rampVars('gold', accent)}`;
		if (!vars) return;
		const css = `:root{${vars}}`;

		let el = this.doc.getElementById('tenant-theme') as HTMLStyleElement | null;
		if (!el) {
			el = this.doc.createElement('style');
			el.id = 'tenant-theme';
			this.doc.head.appendChild(el);
		}
		el.textContent = css;
	}
}
